import { useState, useEffect } from 'react';
import usePageContent from '../hooks/usePageContent';
import { businessInfo } from '../data';

export default function Contact() {
  const { page, loading } = usePageContent('kontakt');
  
  // Form State
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot
  });
  
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (page) {
      document.title = page.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.meta_description);
      }
    }
  }, [page]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Navn skal udfyldes';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer skal udfyldes';
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Ugyldigt telefonnummer';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-mail skal udfyldes';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Ugyldig e-mailadresse';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Besked skal udfyldes';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    
    setStatus('loading');
    setErrorMessage('');
    
    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelsen. Prøv venligst igen.');
      }
      
      setStatus('success');
      setFormData({
        name: '',
        phone: '',
        email: '',
        description: '',
        website_url: ''
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Der opstod en netværksfejl. Prøv venligst igen.');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-beige">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-brand-beige">
      {/* Page Header */}
      <section className="bg-brand-cream/30 py-12 md:py-16 border-b border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
            Kontakt os
          </span>
          <h1 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl lg:text-5xl">
            {page.hero_title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
            {page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Contact Form & Info Grid */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            
            {/* Left Column: Contact Info & Map */}
            <div className="lg:col-span-5 flex flex-col gap-8">
              <div>
                <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark">
                  Klinikoplysninger
                </h2>
                <p className="mt-2 text-sm text-brand-neutral-dark/80">
                  Du er altid velkommen til at ringe eller skrive. Hvis jeg er i gang med en behandling, læg da en besked, så ringer jeg tilbage.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                
                {/* Phone */}
                <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-5 flex gap-4 items-start">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                    <i className="fa-light fa-phone" />
                  </div>
                  <div>
                    <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">Telefon</h4>
                    <p className="text-xs text-brand-neutral-dark/80 mt-1">
                      Ring eller send en SMS:
                    </p>
                    <p className="text-sm font-semibold text-brand-sage-dark mt-1 whitespace-nowrap">
                      {businessInfo.phone}
                    </p>
                  </div>
                </div>

                {/* Email */}
                {businessInfo.email && (
                  <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-5 flex gap-4 items-start">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                      <i className="fa-light fa-envelope" />
                    </div>
                    <div>
                      <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">E-mail</h4>
                      <p className="text-xs text-brand-neutral-dark/80 mt-1">
                        Send en e-mail direkte:
                      </p>
                      <p className="text-sm font-semibold text-brand-sage-dark mt-1 whitespace-nowrap">
                        {businessInfo.email}
                      </p>
                    </div>
                  </div>
                )}

                {/* Address */}
                <div className="rounded-2xl border border-brand-sage-medium/10 bg-white/50 p-5 flex gap-4 items-start">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                    <i className="fa-light fa-location-dot" />
                  </div>
                  <div>
                    <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">Adresse</h4>
                    <p className="text-xs text-brand-neutral-dark/80 mt-1">
                      Klinikkens adresse:
                    </p>
                    <p className="text-sm font-semibold text-brand-neutral-dark mt-1">
                      {businessInfo.address}
                    </p>
                  </div>
                </div>

              </div>

              {/* Opening Hours */}
              <div className="rounded-2xl border border-brand-sage-medium/10 bg-brand-cream/30 p-6">
                <h3 className="font-serif text-base font-semibold text-brand-neutral-dark flex items-center gap-2">
                  <i className="fa-light fa-clock text-brand-sage" /> Åbningstider
                </h3>
                <ul className="mt-4 flex flex-col gap-2.5 text-xs text-brand-neutral-dark/80">
                  {businessInfo.openingHours.map((item, index) => (
                    <li key={index} className="flex justify-between border-b border-brand-sage-medium/10 pb-2 last:border-0 last:pb-0">
                      <span className="font-medium">{item.days}</span>
                      <span>{item.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-brand-sage-medium/15 bg-white/80 p-8 shadow-sm">
                <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark mb-2">
                  Send en besked
                </h2>
                <div 
                  className="prose prose-stone text-xs leading-relaxed text-brand-neutral-dark/80 mb-6"
                  dangerouslySetInnerHTML={{ __html: page.content }}
                />

                {status === 'success' ? (
                  <div className="rounded-2xl bg-brand-sage-light/50 border border-brand-sage-medium/20 p-6 text-center animate-in fade-in duration-300">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-sage text-white mb-4">
                      <i className="fa-light fa-circle-check text-xl" />
                    </div>
                    <h3 className="font-serif text-lg font-semibold text-brand-neutral-dark">Tak for din besked!</h3>
                    <p className="text-xs text-brand-neutral-dark/80 mt-2">
                      Jeg har modtaget din besked og vil vende tilbage til dig så hurtigt som muligt.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                    
                    {/* Honeypot Field - ABSOLUTELY HIDDEN */}
                    <div className="sr-only absolute" aria-hidden="true">
                      <label htmlFor="website_url">Website URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        value={formData.website_url}
                        onChange={handleChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </div>

                    {/* Name */}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="name" className="text-xs font-semibold text-brand-neutral-dark">
                        Navn <span className="text-brand-sage-dark">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white/50 px-4 py-3 text-sm text-brand-neutral-dark transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-sage/50 ${
                          errors.name ? 'border-red-400 focus:ring-red-200' : 'border-brand-sage-medium/20 focus:border-brand-sage'
                        }`}
                        placeholder="Dit fulde navn"
                      />
                      {errors.name && <span className="text-[11px] text-red-500 font-medium">{errors.name}</span>}
                    </div>

                    {/* Phone & Email Row */}
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      {/* Phone */}
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="phone" className="text-xs font-semibold text-brand-neutral-dark">
                          Telefonnummer <span className="text-brand-sage-dark">*</span>
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`w-full rounded-xl border bg-white/50 px-4 py-3 text-sm text-brand-neutral-dark transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-sage/50 ${
                            errors.phone ? 'border-red-400 focus:ring-red-200' : 'border-brand-sage-medium/20 focus:border-brand-sage'
                          }`}
                          placeholder="F.eks. 28 14 38 24"
                        />
                        {errors.phone && <span className="text-[11px] text-red-500 font-medium">{errors.phone}</span>}
                      </div>

                      {/* Email */}
                      <div className="flex flex-col gap-1.5">
                        <label htmlFor="email" className="text-xs font-semibold text-brand-neutral-dark">
                          E-mail <span className="text-brand-sage-dark">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full rounded-xl border bg-white/50 px-4 py-3 text-sm text-brand-neutral-dark transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-sage/50 ${
                            errors.email ? 'border-red-400 focus:ring-red-200' : 'border-brand-sage-medium/20 focus:border-brand-sage'
                          }`}
                          placeholder="F.eks. navn@mail.dk"
                        />
                        {errors.email && <span className="text-[11px] text-red-500 font-medium">{errors.email}</span>}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="description" className="text-xs font-semibold text-brand-neutral-dark">
                        Beskrivelse / Besked <span className="text-brand-sage-dark">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        rows="5"
                        value={formData.description}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-white/50 px-4 py-3 text-sm text-brand-neutral-dark transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-brand-sage/50 ${
                          errors.description ? 'border-red-400 focus:ring-red-200' : 'border-brand-sage-medium/20 focus:border-brand-sage'
                        }`}
                        placeholder="Skriv din besked, spørgsmål eller ønske om tidsbestilling her..."
                      />
                      {errors.description && <span className="text-[11px] text-red-500 font-medium">{errors.description}</span>}
                    </div>

                    {/* Error Message */}
                    {status === 'error' && (
                      <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-xs text-red-600 font-medium">
                        {errorMessage}
                      </div>
                    )}

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="mt-2 inline-flex items-center justify-center rounded-full bg-brand-sage px-8 py-3.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-sage focus:ring-offset-2"
                    >
                      {status === 'loading' ? (
                        <>
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Sender...
                        </>
                      ) : (
                        <>
                          Send besked <i className="fa-light fa-paper-plane ml-2" />
                        </>
                      )}
                    </button>

                  </form>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
